/* AB Maturity Latency isLatencyTest ab_maturity_latency */

import { useState, useEffect, useRef } from 'react';
import { Box, Button, Typography, List, ListItem } from '@evaneos/b2b-ui';
import CircleIcon from '@evaneos/b2b-ui/icons/Circle';
import VisibilityIcon from '@evaneos/b2b-ui/icons/VisibilityRounded';
import cx from 'classnames';

import { FormattedMessage } from 'react-intl';

import { DossierId } from '../../../../resources/dossier/types';
import { useGetTrackedTripProperties, useTrackEventAmplitude } from '../../../../shared/analytics';
import { ABMaturityLatencyExperimentChip } from '../../../../shared/components/ExperimentChip';
import { Message } from '../../../../resources/discussion/types';

import s from './ABMatLatency_NewMessageModal.css';

interface ABMatLatency_NewMessageModalProps {
    isVisible: boolean;
    onClose: () => void;
    dossierId: DossierId;
    travelerMessage: Message | null;
}

export const ABMatLatency_NewMessageModal: React.FC<ABMatLatency_NewMessageModalProps> = ({
    isVisible,
    onClose,
    dossierId,
    travelerMessage,
}) => {
    const [isMessageBlurred, setIsMessageBlurred] = useState(true);
    const hasTrackedDisplay = useRef(false);
    const trackedTripProperties = useGetTrackedTripProperties(dossierId);
    const { trackEvent } = useTrackEventAmplitude();

    // Track display viewed event only once when modal becomes visible
    useEffect(() => {
        if (isVisible && travelerMessage && !hasTrackedDisplay.current) {
            trackEvent((ampli, defaultProperties) => {
                ampli.displayViewedLatencyPopIn({
                    ...defaultProperties,
                    ...trackedTripProperties,
                });
            });
            hasTrackedDisplay.current = true;
        }
    }, [isVisible, travelerMessage, trackEvent, trackedTripProperties]);

    const handleReadMessage = () => {
        setIsMessageBlurred(false);
        trackEvent((ampli, defaultProperties) => {
            ampli.revealMessageLatencyClicked({
                ...defaultProperties,
                ...trackedTripProperties,
            });
        });
    };

    const handleRespond = () => {
        trackEvent((ampli, defaultProperties) => {
            ampli.answerMessageLatencyClicked({
                ...defaultProperties,
                ...trackedTripProperties,
            });
        });
        onClose();
    };

    if (!isVisible || !travelerMessage) return null;

    return (
        <Box
            role="dialog"
            className={s.modalOverlay}
            onClick={(e) => {
                // Prevent closing when clicking on overlay
                e.stopPropagation();
            }}
        >
            <Box className={s.modalContainer} onClick={(e) => e.stopPropagation()}>
                <Box className={s.modalHeader}>
                    <ABMaturityLatencyExperimentChip size="medium" className={s.modalHeaderChip} />
                    <Typography variant="h3" color="white">
                        <FormattedMessage id="ab_maturity_latency.discussion.modal.title" />
                    </Typography>
                </Box>

                <Box className={s.modalContent}>
                    <List dense>
                        <ListItem sx={{ p: 0 }}>
                            <CircleIcon sx={{ fontSize: '6px', color: 'white', mr: 1 }} />
                            <Typography variant="body1" color="white">
                                <FormattedMessage id="ab_maturity_latency.discussion.modal.advice_discover" />
                            </Typography>
                        </ListItem>
                        <ListItem sx={{ p: 0 }}>
                            <CircleIcon sx={{ fontSize: '6px', color: 'white', mr: 1 }} />
                            <Typography variant="body1" color="white">
                                <FormattedMessage id="ab_maturity_latency.discussion.modal.advice_answer" />
                            </Typography>
                        </ListItem>
                    </List>

                    <Box className={s.messageContainer}>
                        <Box className={s.messageBox}>
                            <Box
                                className={cx(s.messageContent, {
                                    [s.messageBlurred]: isMessageBlurred,
                                })}
                            >
                                <Typography variant="body1" className={s.messageText}>
                                    {travelerMessage.body}
                                </Typography>
                            </Box>

                            {isMessageBlurred && (
                                <Box className={s.blurOverlay}>
                                    <Button
                                        variant="contained"
                                        onClick={handleReadMessage}
                                        endIcon={<VisibilityIcon />}
                                    >
                                        <FormattedMessage id="ab_maturity_latency.discussion.modal.read_message_button" />
                                    </Button>
                                </Box>
                            )}
                        </Box>
                    </Box>

                    <Box
                        className={cx(s.respondButtonContainer, {
                            [s.hidden]: isMessageBlurred,
                        })}
                    >
                        <Button
                            variant="contained"
                            onClick={handleRespond}
                            className={s.respondButton}
                        >
                            <FormattedMessage id="ab_maturity_latency.discussion.modal.respond_button" />
                        </Button>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};
